import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Paper, Skeleton, Stack, Tooltip, Typography, alpha } from "@mui/material";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import tripServices from "app/services/trip-services";

// Los KPI de la operación del día, montados en la barra superior a la
// altura del avatar. Al hacer clic, cada uno abre el panel del dashboard que lo
// explica: la tarjeta dice cuántos, el panel dice por qué.
//
// Se montan por portal sobre un slot del header para que el resto de las páginas
// no cargue nada. El header mide 80px, así que las tarjetas van a 56: entran con
// aire y siguen siendo tarjetas, no una línea de texto.
//
// Los colores van apagados a propósito: son indicadores permanentes, no
// alertas. Un rojo saturado en "problemas" grita todo el día y se deja de ver a la
// semana.
// Que panel del dashboard abre cada tarjeta.
const CARDS = [
    {
        key: "por_preparar",
        panel: "por_preparar",
        label: "Por preparar",
        help: "Entregas de hoy que todavía no arrancaron",
        tone: "#B98A2E",
    },
    {
        key: "rutas_iniciadas",
        panel: "rutas_iniciadas",
        label: "Rutas iniciadas",
        help: "Viajes de hoy que ya salieron",
        tone: "#3E6B7A",
    },
    {
        key: "con_problemas",
        panel: "con_problemas",
        label: "Con problemas",
        help: "Canceladas, cerradas fuera de hora, o en curso con la hora ya vencida",
        tone: "#A85B47",
    },
    {
        key: "entregadas",
        panel: "entregadas",
        label: "Entregadas",
        help: "Entregas cerradas hoy, sobre el total planificado para el día",
        tone: "#4E7A5E",
    },
    {
        key: "fuera_de_horario",
        panel: "fuera_de_horario",
        label: "Fuera de horario",
        help: "Pedidos cerrados fuera del horario configurado al conductor; si no tiene, el de la compañía",
        tone: "#6B5B8A",
    },
    {
        key: "km_excedidos",
        panel: "km_planificado",
        label: "Exceden km",
        help: "Viajes terminados que recorrieron más km que el plan, contando solo las paradas que se cerraron",
        tone: "#8A6A4E",
    },
];

// La flecha dice "mejoró" o "empeoró", no "subió" o "bajó": en por preparar bajar
// es bueno y en entregadas es lo contrario. Con la dirección cruda, el verde
// quedaría del lado equivocado la mitad de las veces.
//
// El sentido lo manda el backend en cada KPI (`better`), que es el mismo lugar
// donde se define qué mide cada indicador.
const trend = (better, value, previous) => {
    if (previous === null || previous === undefined) return null;

    const diff = value - previous;
    if (diff === 0 || better === "neutral") return null;

    const subio = diff > 0;
    const mejoro = better === "higher" ? subio : !subio;

    return {
        icon: subio ? ArrowUpwardOutlinedIcon : ArrowDownwardOutlinedIcon,
        color: mejoro ? "success.main" : "warning.main",
    };
};

// `disabled` apaga la tarjeta cuando no hay con que medirla. Es el caso de "Fuera de
// horario" sin jornada cargada: un cero ahi se lee como "todo en orden" cuando en
// realidad no se evaluo nada, y el tooltip dice que falta hacer.
const KpiCard = ({ card, metrics, extra, disabled = false, disabledHelp = null, onOpenPanel }) => {
    const delta = disabled ? null : trend(metrics.better, metrics.month, metrics.previous_month);
    const DeltaIcon = delta?.icon;

    return (
        <Tooltip
            arrow
            title={
                <Box>
                    <Typography variant="caption" display="block">
                        {disabled ? disabledHelp : card.help}
                    </Typography>
                    {!disabled && (
                        <>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Mes {metrics.month} · mes pasado {metrics.previous_month}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Ábrelo en el dashboard.
                            </Typography>
                        </>
                    )}
                </Box>
            }
        >
            <Paper
                elevation={0}
                component="button"
                type="button"
                onClick={disabled ? undefined : () => onOpenPanel(card.panel)}
                disabled={disabled}
                aria-label={disabled ? disabledHelp : `Ver ${card.label} en el dashboard`}
                sx={{
                    display: "block",
                    textAlign: "left",
                    font: "inherit",
                    color: "inherit",
                    minWidth: 132,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    cursor: disabled ? "default" : "pointer",
                    border: "1px solid",
                    borderColor: disabled ? "divider" : alpha(card.tone, 0.28),
                    bgcolor: disabled ? "action.hover" : alpha(card.tone, 0.07),
                    opacity: disabled ? 0.55 : 1,
                    transition: "background-color .15s, border-color .15s",
                    "&:hover": disabled
                        ? {}
                        : { bgcolor: alpha(card.tone, 0.14), borderColor: card.tone },
                    "&:focus-visible": { outline: `2px solid ${card.tone}`, outlineOffset: 2 },
                }}
            >
                <Typography
                    sx={{
                        display: "block",
                        color: disabled ? "text.disabled" : card.tone,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.07em",
                        textTransform: "uppercase",
                        lineHeight: 1.5,
                        whiteSpace: "nowrap",
                    }}
                >
                    {card.label}
                </Typography>

                <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography
                            sx={{
                                fontSize: 24,
                                fontWeight: 700,
                                lineHeight: 1.15,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {disabled ? "—" : metrics.today}
                        </Typography>
                        {!disabled && extra !== undefined && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                / {extra}
                            </Typography>
                        )}
                    </Stack>

                    {/*
                      El número del mes lleva la palabra "mes" al lado: un "23"
                      suelto junto al 0 de hoy no dice de qué es.
                      Sin flecha (KPI neutro o mes igual al anterior) igual se
                      muestra, en gris: si no, esa tarjeta queda vacía a la derecha
                      y pesa distinto que las otras tres.
                    */}
                    {!disabled && metrics.previous_month !== null && metrics.previous_month !== undefined && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.25}
                            sx={{ color: delta ? delta.color : "text.secondary", flexShrink: 0 }}
                        >
                            {DeltaIcon && <Box component={DeltaIcon} sx={{ fontSize: 12 }} />}
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
                            >
                                {metrics.month} mes
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Tooltip>
    );
};

const TripKpiCards = ({ reloadToken }) => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [slot, setSlot] = useState(null);

    // El slot lo dibuja el header del layout, así que puede no existir todavía en
    // el primer render de la página.
    useEffect(() => {
        setSlot(document.getElementById("header-page-slot"));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        tripServices
            .getKpis(moment().format("YYYY-MM-DD"))
            .then((result) => {
                if (!cancelled) setData(result);
            })
            .catch(() => {
                // Sin permiso o sin datos: la barra simplemente no aparece.
                if (!cancelled) setData(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [reloadToken]);

    if (!slot) return null;

    if (loading) {
        return createPortal(
            <Stack direction="row" spacing={1.25}>
                {CARDS.map((card) => (
                    <Skeleton key={card.key} variant="rounded" width={132} height={56} />
                ))}
            </Stack>,
            slot
        );
    }

    if (!data?.cards) return null;

    return createPortal(
        <Stack direction="row" spacing={1.25} sx={{ minWidth: 0, overflowX: "auto", py: 0.5 }}>
            {CARDS.map((card) => {
                const metrics = data.cards[card.key];
                if (!metrics) return null;

                return (
                    <KpiCard
                        key={card.key}
                        card={card}
                        metrics={metrics}
                        extra={card.key === "entregadas" ? data.planned_delivery_today : undefined}
                        disabled={card.key === "fuera_de_horario" && data.workday_configured === false}
                        disabledHelp={"Debes asignar un horario al conductor en su configuración"}
                        onOpenPanel={(panel) => navigate(`/business-dashboard?panel=${panel}`)}
                    />
                );
            })}
        </Stack>,
        slot
    );
};

export default TripKpiCards;
